import { useCallback } from 'react';
import despia from 'despia-native';

// Check if we're in Despia native context
const isDespia = typeof navigator !== 'undefined' && navigator.userAgent.includes('despia');

export interface NativeGestureOptions {
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onPullDown?: () => void;
  hapticFeedback?: boolean;
  threshold?: number;
}

export const useNativeGestures = (options: NativeGestureOptions = {}) => {
  const {
    onSwipeDown,
    onSwipeUp,
    onSwipeLeft,
    onSwipeRight,
    onPullDown,
    hapticFeedback = true,
    threshold = 50
  } = options;

  // Haptic feedback functions using Despia
  const triggerLight = useCallback(() => {
    if (hapticFeedback && isDespia) {
      try {
        despia('lighthaptic://');
      } catch {
        // Silently fail
      }
    }
  }, [hapticFeedback]);

  const triggerMedium = useCallback(() => {
    if (hapticFeedback && isDespia) {
      try {
        despia('heavyhaptic://');
      } catch {
        // Silently fail
      }
    }
  }, [hapticFeedback]);

  const triggerHeavy = useCallback(() => {
    if (hapticFeedback && isDespia) {
      try {
        despia('heavyhaptic://');
      } catch {
        // Silently fail
      }
    }
  }, [hapticFeedback]);

  const triggerSuccess = useCallback(() => {
    if (hapticFeedback && isDespia) {
      try {
        despia('successhaptic://');
      } catch {
        // Silently fail
      }
    }
  }, [hapticFeedback]);

  // Enhanced gesture handlers with haptic feedback
  const handleSwipeStart = useCallback(() => {
    triggerLight();
  }, [triggerLight]);

  const handleSwipeEnd = useCallback((direction: string) => {
    triggerMedium();
    
    switch (direction) {
      case 'down':
        onSwipeDown?.();
        break;
      case 'up':
        onSwipeUp?.();
        break;
      case 'left':
        onSwipeLeft?.();
        break;
      case 'right':
        onSwipeRight?.();
        break;
    }
  }, [onSwipeDown, onSwipeUp, onSwipeLeft, onSwipeRight, triggerMedium]);

  const handlePullDown = useCallback(() => {
    triggerHeavy();
    onPullDown?.();
  }, [onPullDown, triggerHeavy]);

  // Native-style momentum calculation
  const calculateMomentum = useCallback((velocity: number, distance: number) => {
    const deceleration = 0.0006;
    const momentum = Math.abs(velocity) / deceleration;
    return Math.min(momentum, Math.abs(distance) * 4);
  }, []);

  return {
    triggerLight,
    triggerMedium,
    triggerHeavy,
    triggerSuccess,
    handleSwipeStart,
    handleSwipeEnd,
    handlePullDown,
    calculateMomentum,
    isNativePlatform: isDespia
  };
};
